import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import Layout from '@/components/Layout';
import { apiService } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Search, ChevronLeft, ChevronRight, FileText } from 'lucide-react';

const ACTIONS = [
  { value: '', label: 'Todas' },
  { value: 'CAMPAIGN_CREATE', label: 'Campanha: criar' },
  { value: 'CAMPAIGN_UPDATE', label: 'Campanha: atualizar' },
  { value: 'CAMPAIGN_START', label: 'Campanha: iniciar' },
  { value: 'CAMPAIGN_PAUSE', label: 'Campanha: pausar' },
  { value: 'CAMPAIGN_CANCEL', label: 'Campanha: cancelar' },
  { value: 'CAMPAIGN_SCHEDULE', label: 'Campanha: agendar' },
  { value: 'CAMPAIGN_RESUME', label: 'Campanha: retomar' },
  { value: 'CAMPAIGN_UPLOAD_CONTACTS', label: 'Campanha: upload contatos' },
  { value: 'CAMPAIGN_UPLOAD_MEDIA', label: 'Campanha: upload mídia' },
  { value: 'DISPARO_AVULSO_CREATE', label: 'Disparo avulso: criar' },
  { value: 'DISPARO_AVULSO_INICIAR', label: 'Disparo avulso: iniciar' },
  { value: 'DISPARO_AVULSO_CANCELAR', label: 'Disparo avulso: cancelar' },
  { value: 'WHATSAPP_CONNECTION_CREATE', label: 'Conexão WhatsApp: criar' },
  { value: 'WHATSAPP_CONNECTION_UPDATE', label: 'Conexão WhatsApp: atualizar' },
  { value: 'WHATSAPP_CONNECTION_DELETE', label: 'Conexão WhatsApp: excluir' },
  { value: 'CLIENT_CREATE', label: 'Cliente: criar' },
  { value: 'CLIENT_UPDATE', label: 'Cliente: atualizar' },
  { value: 'CLIENT_DELETE', label: 'Cliente: excluir' },
];

const RESOURCE_TYPES = [
  { value: '', label: 'Todos' },
  { value: 'campaign', label: 'Campanha' },
  { value: 'disparo_avulso', label: 'Disparo avulso' },
  { value: 'whatsapp_connection', label: 'Conexão WhatsApp' },
  { value: 'client', label: 'Cliente' },
];

function formatDate(d: string | Date) {
  const date = new Date(d);
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const AuditLogs = () => {
  const { user } = useAuth();
  const [list, setList] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [action, setAction] = useState('');
  const [resourceType, setResourceType] = useState('');
  const [userId, setUserId] = useState('');
  const [clientId, setClientId] = useState('');
  const [page, setPage] = useState(1);
  const limit = 20;

  const [clients, setClients] = useState<{ id: number; legal_name: string }[]>([]);
  const isAdmin = user?.role === 'admin';

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = { page, limit };
      if (from) params.from = from;
      if (to) params.to = to;
      if (action) params.action = action;
      if (resourceType) params.resource_type = resourceType;
      if (userId) params.user_id = parseInt(userId, 10);
      if (isAdmin && clientId) params.client_id = parseInt(clientId, 10);

      const data = await apiService.getAuditLogs(params);
      setList(data.list || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 0);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao carregar log de auditoria';
      setError(msg);
      setList([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [page, limit, from, to, action, resourceType, userId, clientId, isAdmin]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    if (isAdmin) {
      apiService.getClients().then((arr) => {
        setClients(Array.isArray(arr) ? arr.map((c: any) => ({ id: c.id, legal_name: c.legal_name || c.trade_name || `Cliente ${c.id}` })) : []);
      }).catch(() => setClients([]));
    }
  }, [isAdmin]);

  const handleSearch = () => {
    setPage(1);
  };

  return (
    <Layout>
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Log de Auditoria</h1>
          <p className="text-muted-foreground mt-1">
            Histórico de ações críticas (campanhas, disparos, conexões, clientes). Filtre por data, usuário, ação e tipo de recurso.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Filtros</CardTitle>
            <CardDescription>Defina o período e os critérios para listar os registros.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Data inicial</Label>
                <Input
                  type="date"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Data final</Label>
                <Input
                  type="date"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Ação</Label>
                <Select value={action === '' ? '__all__' : action} onValueChange={(v) => setAction(v === '__all__' ? '' : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todas" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todas</SelectItem>
                    {ACTIONS.filter((a) => a.value).map((a) => (
                      <SelectItem key={a.value} value={a.value}>
                        {a.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Tipo de recurso</Label>
                <Select value={resourceType === '' ? '__all__' : resourceType} onValueChange={(v) => setResourceType(v === '__all__' ? '' : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Todos</SelectItem>
                    {RESOURCE_TYPES.filter((r) => r.value).map((r) => (
                      <SelectItem key={r.value} value={r.value}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>ID do usuário</Label>
                <Input
                  type="number"
                  placeholder="Opcional"
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  min={1}
                />
              </div>
              {isAdmin && (
                <div className="space-y-2">
                  <Label>Cliente</Label>
                  <Select value={clientId === '' ? '__all__' : clientId} onValueChange={(v) => setClientId(v === '__all__' ? '' : v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">Todos</SelectItem>
                      {clients.map((c) => (
                        <SelectItem key={c.id} value={String(c.id)}>
                          {c.legal_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <Button onClick={handleSearch} className="gap-2">
              <Search className="h-4 w-4" />
              Buscar
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">Registros</CardTitle>
              <CardDescription>
                {total} registro(s) encontrado(s)
                {totalPages > 0 && ` • Página ${page} de ${totalPages}`}
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            {error && (
              <div className="rounded-md bg-destructive/10 text-destructive px-4 py-3 text-sm mb-4">
                {error}
              </div>
            )}

            {loading ? (
              <div className="py-8 text-center text-muted-foreground">Carregando...</div>
            ) : list.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground flex flex-col items-center gap-2">
                <FileText className="h-10 w-10" />
                Nenhum registro encontrado para os filtros informados.
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data/Hora</TableHead>
                      <TableHead>Usuário</TableHead>
                      {isAdmin && <TableHead>Cliente</TableHead>}
                      <TableHead>Ação</TableHead>
                      <TableHead>Recurso</TableHead>
                      <TableHead>ID recurso</TableHead>
                      <TableHead className="max-w-[120px]">Detalhes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {list.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell className="whitespace-nowrap text-muted-foreground">
                          {formatDate(row.created_at)}
                        </TableCell>
                        <TableCell>
                          {row.user ? (
                            <span title={row.user.email}>
                              {row.user.name || row.user.email || `#${row.user_id}`}
                            </span>
                          ) : (
                            `#${row.user_id ?? '-'}`
                          )}
                        </TableCell>
                        {isAdmin && (
                          <TableCell>
                            {row.client ? row.client.legal_name : '-'}
                          </TableCell>
                        )}
                        <TableCell className="font-medium">{row.action}</TableCell>
                        <TableCell>{row.resource_type}</TableCell>
                        <TableCell>{row.resource_id ?? '-'}</TableCell>
                        <TableCell className="max-w-[120px] truncate" title={row.payload ? JSON.stringify(row.payload) : ''}>
                          {row.payload ? (
                            <span className="text-muted-foreground text-xs">
                              {typeof row.payload === 'object'
                                ? Object.keys(row.payload).length
                                  ? JSON.stringify(row.payload).slice(0, 60) + (JSON.stringify(row.payload).length > 60 ? '…' : '')
                                  : '-'
                                : String(row.payload)}
                            </span>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4">
                    <p className="text-sm text-muted-foreground">
                      Página {page} de {totalPages} ({total} registros)
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page <= 1 || loading}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Anterior
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page >= totalPages || loading}
                      >
                        Próxima
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AuditLogs;
